import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { CopyButton } from '@components/copy-button';
import { createForm } from '@components/form';
import { FormLabel } from '@components/form-label';
import { IconEmailAddress } from '@components/icons/email-address';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconLoopRestart } from '@components/icons/loop-restart';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import {
  useCreateDnsDomain,
  useInvalidateEmailDomains,
  useVerifyDnsDomain,
} from 'api/company/hooks';
import { DnsRecord, DnsDomain } from 'api/company/requests';

type ManagedFormProps = {
  domain?: DnsDomain;
};

export const useManagedEmailDomainFormSidebar = () =>
  useSidebar(ManagedEmailDomainForm, { variant: 'REGULAR' });

type FormValues = {
  domain: string;
};

const Form = createForm<FormValues>();

type VerifyStatus = 'unverified' | 'verified' | 'error';

const ManagedEmailDomainForm = ({ domain }: ManagedFormProps) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateDnsDomain();
  const verifyDnsDomainMutation = useVerifyDnsDomain();
  const { invalidate } = useInvalidateEmailDomains();
  const isEditing = Boolean(domain?.shortId);
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>(domain?.dnsRecords ?? []);
  const [newShortId, setNewShortId] = useState<string>(domain?.shortId ?? '');
  const [dnsStatus, setDnsStatus] = useState<VerifyStatus>(
    domain?.isVerified ? 'verified' : 'unverified'
  );
  const [isInitialVerifying, setIsInitialVerifying] = useState(isEditing);

  const [errors, setErrors] = useState<Partial<Record<'domain', string>>>({});

  useEffect(() => {
    if (isEditing && domain?.shortId) {
      verifyDnsDomainMutation.mutateAsync(domain.shortId).then((response) => {
        setDnsRecords(response.dnsRecords ?? []);
        setDnsStatus(response.isVerified ? 'verified' : 'unverified');
        setIsInitialVerifying(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain?.shortId, isEditing]);

  const submit = async (values: FormValues) => {
    setErrors({});

    try {
      if (isEditing && domain?.shortId) {
        return;
      }
      const response = await createMutation.mutateAsync({
        domain: values.domain,
      });
      setDnsRecords(response.dnsRecords ?? []);
      setNewShortId(response.shortId);
      invalidate();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const responseError = error as { response?: { data?: { domain?: string } } };
        if (responseError.response?.data?.domain) {
          setErrors({ domain: responseError.response.data.domain });
        }
      }
    }
  };

  const verifyDomain = async () => {
    const response = await verifyDnsDomainMutation.mutateAsync(newShortId ?? domain?.shortId ?? '');
    if (response.isVerified) {
      setDnsStatus('verified');
    } else {
      setDnsStatus('error');
    }
    setDnsRecords(response.dnsRecords ?? []);
  };

  const isLoading = createMutation.isLoading || verifyDnsDomainMutation.isLoading;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconEmailAddress}
        iconColor="elementBlue100"
        title={isEditing ? t('editEmailDomain') : t('addEmailDomain')}
        subtitle={t('dns')}
      />
      <Sidebar.Content>
        {isLoading && <Sidebar.Spinner />}
        <Form onSubmit={submit} overrideErrors={errors}>
          <Stack space={6}>
            <Text color="textPrimary" font="regular14" text={t('description')} />
            <Separator />
            <Stack space={4}>
              <Stack space={4} orientation="horizontal" align="center">
                <StepCounter>1</StepCounter>
                <Text color="textPrimary" font="semibold14" text={t('enterCustomDomain')} />
              </Stack>
              <Stack space={2} orientation="horizontal">
                <Form.TextField
                  id="domain"
                  label={t('domainLabel')}
                  hideLabel
                  validators={['required']}
                  placeholder={t('domainPlaceholder')}
                  defaultValue={domain?.domain}
                  isDisabled={isEditing || isLoading}
                  isFullWidth
                />
                {!isEditing && (
                  <Form.Submit
                    text={t('core.save')}
                    variant="primary-blue"
                    isDisabled={isLoading}
                  />
                )}
              </Stack>
            </Stack>
            <Separator />
            <Stack space={4}>
              <Stack space={4} orientation="horizontal" align="center">
                <StepCounter>2</StepCounter>
                <Text color="textPrimary" font="semibold14" text={t('setUpDNSRecord')} />
              </Stack>
              <Text color="textSecondary" font="regular14" text={t('dnsRecordDescription')} />
            </Stack>
            {dnsRecords.length > 0 || isInitialVerifying ? (
              <Stack space={6}>
                {dnsRecords.map((record) => (
                  <div key={record.name}>
                    <Stack space={4}>
                      <div>
                        <FormLabel text={t('dnsRecordType')} />
                        <Text font="regular14" color="textPrimary" text={record.recordType} />
                      </div>
                      <div>
                        <FormLabel text={t('dnsHostname')} />
                        <Text font="regular14" color="textPrimary" text={record.name} />
                      </div>
                      <div>
                        <FormLabel text={t('dnsValue')} />
                        <Text font="regular14" color="textPrimary" text={record.value} />
                      </div>
                      <Stack orientation="horizontal" space={4}>
                        <CopyButton copyLabel={t('dnsHostname')} getText={() => record.name} />
                        <CopyButton copyLabel={t('dnsValue')} getText={() => record.value} />
                      </Stack>
                      <Separator />
                    </Stack>
                  </div>
                ))}
                <Stack space={4}>
                  <Stack space={4} orientation="horizontal" align="center">
                    <StepCounter>3</StepCounter>
                    <Text color="textPrimary" font="semibold14" text={t('verifyDNSRecord')} />
                  </Stack>
                  <Text
                    color="textSecondary"
                    font="regular14"
                    text={t('verifyDNSRecordDescription')}
                  />
                  <Stack orientation="horizontal">
                    <Button
                      variant="primary-blue"
                      iconLeft={<IconLoopRestart />}
                      onClick={verifyDomain}
                      text={t('verifyDNS')}
                      isDisabled={isLoading}
                    />
                  </Stack>
                </Stack>
                {verifyDnsDomainMutation.isLoading ? (
                  <RelativeSpinner />
                ) : (
                  <VerifiedStatus status={dnsStatus} records={dnsRecords} />
                )}
                {dnsStatus !== 'verified' && (
                  <>
                    <Separator />
                    <Card space={3} variant="indigo" hasBorder={false}>
                      <Stack space={2} orientation="horizontal">
                        <IconFormIndicatorInfo color="elementIndigo100" />
                        <Text color="elementIndigo200" font="regular14" text={t('verifyLater')} />
                      </Stack>
                    </Card>
                  </>
                )}
              </Stack>
            ) : (
              <Card space={6} variant="gray">
                <Stack space={2} orientation="horizontal" justify="center">
                  <IconEmailAddress color="elementPurple80" />
                  <Text color="textSecondary" font="semibold12" text={t('noDomain')} />
                </Stack>
              </Card>
            )}
          </Stack>
          <Sidebar.Footer>
            <Form.Actions>
              <Button
                text={t('core.close')}
                isDisabled={isLoading}
                onClick={hideSidebar}
                variant="secondary"
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const VerifiedStatus = ({ status, records }: { status: VerifyStatus; records: DnsRecord[] }) => {
  const { t } = useScopedTranslate(locale);
  switch (status) {
    case 'unverified':
      return (
        <Stack space={2} orientation="horizontal" align="center">
          <IconFormIndicatorInfo color="elementIndigo100" size={12} />
          <Text color="elementIndigo200" font="regular14" text={t('unverified')} />
        </Stack>
      );
    default:
      return (
        <Stack space={4}>
          {records.map((record) => {
            const isValid = record.valid === 'valid';
            return (
              <Stack space={2} orientation="horizontal" key={record.name}>
                {isValid ? (
                  <IconFormIndicatorSuccess size={16} />
                ) : (
                  <IconFormIndicatorError size={16} />
                )}
                <Text
                  color={isValid ? 'green200' : 'red100'}
                  font="regular14"
                  text={t(isValid ? 'verificationSuccess' : 'verificationError', {
                    domainName: record.name,
                  })}
                />
              </Stack>
            );
          })}
        </Stack>
      );
  }
};

const StepCounter = styled.div({
  display: 'flex',
  font: theme.font.semibold12,
  color: theme.color.white100,
  backgroundColor: theme.color.elementBlue100,
  borderRadius: '50%',
  height: '1.5rem',
  width: '1.5rem',
  alignItems: 'center',
  justifyContent: 'center',
});

const locale = {
  en: {
    addEmailDomain: 'Add email domain',
    editEmailDomain: 'Edit email domain',
    dns: '(DNS)',
    enterCustomDomain: 'Enter your custom domain',
    setUpDNSRecord: 'Set up TXT records with your DNS provider',
    addDomain: 'Add domain',
    dnsRecordType: 'Type',
    dnsHostname: 'Hostname',
    dnsValue: 'Value',
    noDomain: 'No domain added',
    verifyDNS: 'Verify DNS',
    verifyDNSRecord: 'Verify DNS record',
    verifyDNSRecordDescription:
      'Check to see if your records are verified. It can take some time to update DNS records',
    verifyLater:
      'Please note: You can close the sidebar and verify it later, you can still use the default method until it becomes available',
    unverified: 'DNS not verified',
    verificationSuccess: '{domainName} verified',
    verificationError:
      '{domainName} verification unsuccessful, please veirfy that you have the correct records add or try again later. Updating DNS records can take some time',
    domainLabel: 'Domain',
    domainPlaceholder: 'acme.com',
    description: 'To use a custom domain for your Journeys emails, please follow these steps',
    dnsRecordDescription:
      'To configure your custom domain, add a TXT record with your DNS provider that points your custom domain to your Journeys subdomain with your DNS provider.',
  },
};
